import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ChevronDown, ChevronUp, Check, X as XIcon, AlertTriangle } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useHolidays } from '@/hooks/useHolidays';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { DateLabel } from '@/components/DateLabel';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { TimeRangeInput, type TimeRangeInputValue } from '@/components/TimeRangeInput';
import { buildLocalIso, isoToLocalHm, personKey, type PersonRef } from '@/lib/shift-format';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/shift/urgent` (v0.4 章④ F-5、§5-19-2)。急な出勤要請 (当日欠勤 → 欠員 → 応援依頼 → 応諾)。
 * 設計: docs/design/v04-shift-labor-management-detail.md §4-8 / §4-10
 *
 * ⚠️ 主に店長・エリアマネージャー向けの画面。`GET /urgent-requests` は node_id 未指定時
 * `resolveViewScopeStoreIds` でスコープ解決するため、self scope のみ (パート従業員) の場合は
 * 常に空配列になる (既存バックエンドの制約、店舗スコープを持つ店長・上位者が要請を作成・充足する
 * 運用を前提とする。個人が通知経由で直接応諾するフローは通知本文へのリンクに依存する想定で、
 * この一覧画面をブラウズすることは前提にしていない — 次イテレーションの検討事項)。
 * ⚠️ condition_set_id (候補の絞り込み条件) はこの画面では設定しない (§6d 過剰設計をしない、
 * 必要になった時点で `/settings/staffing` の ConditionSetEditor と同型で追加する)。
 */

const REASON_LABELS: Record<string, string> = { absence: '欠員 (当日欠勤等)', event_demand: 'イベント等による増員', other: 'その他' };
const STATUS_LABELS: Record<string, string> = { open: '募集中', filled: '充足済み', cancelled: 'キャンセル', expired: '期限切れ' };
const STATUS_BADGE_CLS: Record<string, string> = {
  open: 'bg-alert-bg text-alert-text', filled: 'bg-success-bg text-success-text',
  cancelled: 'bg-surface-sunken text-text-sub', expired: 'bg-surface-sunken text-text-sub',
};

type UrgentRequest = {
  request_id: string; node_id: string; target_date: string; start_at: string; end_at: string;
  reason: string; status: string; filled_assignment_id: string | null; created_at: string;
};
type UrgentResponse = { response_id: string; employee_id: string | null; external_person_id: string | null; response: string; responded_at: string };
type RosterPerson = { employee_id: string | null; external_person_id: string | null; display_name: string };

function NodeSelect({ nodeId, onChange, placeholder }: { nodeId: string; onChange: (id: string) => void; placeholder: string }) {
  const { data } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flat = useMemo(() => flattenNodes(data?.roots ?? []), [data]);
  return (
    <Select value={nodeId || undefined} onValueChange={onChange}>
      <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {flat.map(({ node, depth }) => <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// -----------------------------------------------------------------------------
// 新規要請の作成
// -----------------------------------------------------------------------------
function CreateRequestModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [nodeId, setNodeId] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState<TimeRangeInputValue>({ startTime: '09:00', endTime: '17:00' });
  const [reason, setReason] = useState<keyof typeof REASON_LABELS>('absence');

  const createMut = useMutation({
    mutationFn: () => apiFetch('/urgent-requests', {
      method: 'POST',
      body: JSON.stringify({
        node_id: nodeId, target_date: date, reason,
        start_at: buildLocalIso(date, time.startTime ?? '00:00'),
        end_at: buildLocalIso(date, time.endTime ?? '00:00'),
      }),
    }),
    onSuccess: () => { toast.success('緊急応援要請を作成しました'); onDone(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '作成に失敗しました'),
  });

  const canSave = !!nodeId && !!date && !!time.startTime && !!time.endTime;

  return (
    <Modal
      open title="緊急応援要請を作成" onClose={onClose} size="sm"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave || createMut.isPending} loading={createMut.isPending} onClick={() => createMut.mutate()}>作成</Button>
        </>
      )}
    >
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-text-sub mb-1">対象店舗</label>
          <NodeSelect nodeId={nodeId} onChange={setNodeId} placeholder="店舗を選ぶ" />
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1">対象日</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring" />
        </div>
        <TimeRangeInput value={time} onChange={setTime} legend="必要な時間帯" />
        <div>
          <label className="block text-xs text-text-sub mb-1">理由</label>
          <Select value={reason} onValueChange={(v) => setReason(v as typeof reason)}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(REASON_LABELS).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// 充足 (既存の割当に紐づける)
// -----------------------------------------------------------------------------
type Period = { period_id: string; start_date: string; end_date: string };
type BoardAssignment = PersonRef & { assignment_id: string; person_name: string | null; work_date: string; start_at: string; end_at: string };

function FillModal({ request, onClose, onDone }: { request: UrgentRequest; onClose: () => void; onDone: () => void }) {
  const { data: periodsData } = useQuery<{ periods: Period[] }>({ queryKey: ['shift-periods'], queryFn: () => apiFetch('/shift/periods') });
  const coveringPeriod = (periodsData?.periods ?? []).find((p) => p.start_date <= request.target_date && request.target_date <= p.end_date);

  const { data: boardData, isLoading } = useQuery<{ assignments: BoardAssignment[] }>({
    queryKey: ['shift-board-for-fill', coveringPeriod?.period_id, request.node_id],
    queryFn: () => apiFetch(`/shift/periods/${coveringPeriod!.period_id}/nodes/${request.node_id}/board`),
    enabled: !!coveringPeriod,
  });
  const candidates = (boardData?.assignments ?? []).filter((a) => a.work_date === request.target_date);
  const [assignmentId, setAssignmentId] = useState('');

  const fillMut = useMutation({
    mutationFn: () => apiFetch(`/urgent-requests/${request.request_id}/fill`, { method: 'POST', body: JSON.stringify({ assignment_id: assignmentId }) }),
    onSuccess: () => { toast.success('充足済みにしました'); onDone(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  return (
    <Modal
      open title="充足済みにする" onClose={onClose} size="sm"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!assignmentId || fillMut.isPending} loading={fillMut.isPending} onClick={() => fillMut.mutate()}>充足済みにする</Button>
        </>
      )}
    >
      {!coveringPeriod ? (
        <p className="text-sm text-text-sub">対象日を含むシフト期間が見つかりません。編成ボードで割当を作成してから、あらためてお試しください。</p>
      ) : isLoading ? (
        <div className="text-text-sub text-sm">読み込み中…</div>
      ) : candidates.length === 0 ? (
        <p className="text-sm text-text-sub">この日にこの店舗の割当がありません。編成ボードで先に割当を作成してください。</p>
      ) : (
        <div className="space-y-1">
          {candidates.map((a) => (
            <label key={a.assignment_id} className="flex items-center gap-2 text-sm px-2 py-2 border border-surface-border rounded min-h-[44px]">
              <input type="radio" name="fill-assignment" checked={assignmentId === a.assignment_id} onChange={() => setAssignmentId(a.assignment_id)} />
              <span>{a.person_name ?? '(氏名不明)'}</span>
              <span className="text-text-sub tabular-nums">{isoToLocalHm(a.start_at)}〜{isoToLocalHm(a.end_at)}</span>
            </label>
          ))}
        </div>
      )}
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// 個々の要請カード
// -----------------------------------------------------------------------------
function RequestCard({ req, onChanged }: { req: UrgentRequest; onChanged: () => void }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [filling, setFilling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // ⚠️ QA指摘反映: employee_id/external_person_id は別フィールドであり、personKey() で作った
  // 単一文字列 state に丸めると外部人材選択時に employee_id へ external_person_id が誤送信され
  // verifyEmployeeInTenant に必ず弾かれ常に404になっていた。選択したロースター行そのものを保持する。
  const [respondPersonKey, setRespondPersonKey] = useState('');
  const holidays = useHolidays(req.target_date, req.target_date, req.node_id);

  const { data: responsesData } = useQuery<{ responses: UrgentResponse[] }>({
    queryKey: ['urgent-responses', req.request_id],
    queryFn: () => apiFetch(`/urgent-requests/${req.request_id}/responses`),
    enabled: expanded,
  });
  const { data: rosterData } = useQuery<{ people: RosterPerson[] }>({
    queryKey: ['shift-roster', req.node_id], queryFn: () => apiFetch(`/standing-patterns?node_id=${req.node_id}`),
    enabled: expanded, retry: false,
  });
  const roster = rosterData?.people ?? [];
  const nameByKey = useMemo(() => new Map(roster.map((p) => [personKey(p), p.display_name])), [roster]);
  const respondPerson = roster.find((p) => personKey(p) === respondPersonKey) ?? null;

  const respondMut = useMutation({
    mutationFn: (response: 'accepted' | 'declined') => apiFetch(`/urgent-requests/${req.request_id}/respond`, {
      method: 'POST',
      body: JSON.stringify({
        response, employee_id: respondPerson?.employee_id ?? null, external_person_id: respondPerson?.external_person_id ?? null,
      }),
    }),
    onSuccess: () => {
      toast.success('応答を記録しました');
      setRespondPersonKey('');
      // ⚠️ 応答一覧は ['urgent-requests'] とは別の queryKey (['urgent-responses', request_id]) のため、
      // onChanged() (要請一覧の再取得) だけでは反映されず「保存できたのに一覧は古いまま」になる
      // (F-2〜F-5 staging実機検証で発見、応答は保存できているがUIに反映されない)
      qc.invalidateQueries({ queryKey: ['urgent-responses', req.request_id] });
      onChanged();
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '記録に失敗しました'),
  });

  const cancelMut = useMutation({
    mutationFn: () => apiFetch(`/urgent-requests/${req.request_id}/cancel`, { method: 'POST' }),
    onSuccess: () => { toast.success('要請をキャンセルしました'); setCancelling(false); onChanged(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'キャンセルに失敗しました'),
  });

  return (
    <div className="border border-surface-border rounded-lg bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${STATUS_BADGE_CLS[req.status] ?? ''}`}>{STATUS_LABELS[req.status] ?? req.status}</span>
            <span className="text-sm font-medium text-text-body"><DateLabel date={req.target_date} withWeekday holidays={holidays} /></span>
            <span className="text-sm text-text-sub tabular-nums">{isoToLocalHm(req.start_at)}〜{isoToLocalHm(req.end_at)}</span>
          </div>
          <p className="text-xs text-text-sub mt-1">{REASON_LABELS[req.reason] ?? req.reason}</p>
        </div>
        {/* design-expert Medium指摘反映: shrink-0 + flex-wrap無しだと最大3要素(充足済みにする/
            キャンセル/開閉トグル)が320px幅で収まりきらずクリップしうる。折り返し可能にする */}
        <div className="flex items-center flex-wrap gap-2">
          {req.status === 'open' && (
            <>
              <Button variant="secondary" size="sm" onClick={() => setFilling(true)}>充足済みにする</Button>
              <Button variant="ghost" size="sm" onClick={() => setCancelling(true)}>キャンセル</Button>
            </>
          )}
          <Button variant="ghost" size="sm" aria-label={expanded ? '閉じる' : '応答を見る'} onClick={() => setExpanded((v) => !v)}>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-surface-border space-y-3">
          <div>
            <h4 className="text-xs font-bold text-text-sub mb-1">応答</h4>
            {(responsesData?.responses ?? []).length === 0 ? (
              <p className="text-xs text-text-sub">まだ応答がありません。</p>
            ) : (
              <ul className="text-sm space-y-1">
                {(responsesData?.responses ?? []).map((r) => (
                  <li key={r.response_id} className="flex items-center gap-2">
                    {r.response === 'accepted' ? <Check size={14} className="text-success-text" aria-hidden /> : <XIcon size={14} className="text-text-sub" aria-hidden />}
                    <span>{nameByKey.get(personKey(r)) ?? '(氏名不明)'}</span>
                    <span className="text-xs text-text-sub">{r.response === 'accepted' ? '応諾' : '辞退'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {req.status === 'open' && (
            <div>
              <h4 className="text-xs font-bold text-text-sub mb-1">代理で応答を記録</h4>
              <div className="flex flex-wrap items-center gap-2">
                <div className="w-56">
                  <Select value={respondPersonKey || undefined} onValueChange={setRespondPersonKey}>
                    <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="対象者を選ぶ" /></SelectTrigger>
                    <SelectContent>
                      {roster.map((p) => <SelectItem key={personKey(p)} value={personKey(p)}>{p.display_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="secondary" size="sm" disabled={!respondPerson || respondMut.isPending} onClick={() => respondMut.mutate('accepted')}>応諾で記録</Button>
                <Button variant="ghost" size="sm" disabled={!respondPerson || respondMut.isPending} onClick={() => respondMut.mutate('declined')}>辞退で記録</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {filling && <FillModal request={req} onClose={() => setFilling(false)} onDone={() => { setFilling(false); onChanged(); }} />}
      <ConfirmDialog
        open={cancelling} title="この要請をキャンセルしますか？" destructive confirmLabel="キャンセルする"
        loading={cancelMut.isPending} onClose={() => setCancelling(false)} onConfirm={() => cancelMut.mutate()}
        message={<p>この緊急応援要請をキャンセルします。既にあった応答は記録として残ります。</p>}
      />
    </div>
  );
}

export function ShiftUrgentPage() {
  const qc = useQueryClient();
  const [nodeId, setNodeId] = useState('');
  const [status, setStatus] = useState<'open' | 'filled' | 'cancelled' | 'expired' | ''>('open');
  const [creating, setCreating] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<{ requests: UrgentRequest[] }>({
    queryKey: ['urgent-requests', nodeId, status],
    queryFn: () => apiFetch(`/urgent-requests?${nodeId ? `node_id=${nodeId}&` : ''}${status ? `status=${status}` : ''}`),
  });
  const requests = data?.requests ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ['urgent-requests'] });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-h1">急な出勤要請</h1>
          <p className="text-sm text-text-sub mt-1">当日欠勤等による欠員を店舗内・近隣店舗へ知らせ、応援を募ります。</p>
        </div>
        <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setCreating(true)}>要請を作成</Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="w-64">
          <label className="block text-xs text-text-sub mb-1">店舗で絞り込み</label>
          <NodeSelect nodeId={nodeId} onChange={setNodeId} placeholder="全店舗 (自分のスコープ内)" />
        </div>
        <div className="w-40">
          <label className="block text-xs text-text-sub mb-1">状態</label>
          <Select value={status || '__all__'} onValueChange={(v) => setStatus(v === '__all__' ? '' : (v as typeof status))}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">すべて</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : isError ? (
        <div className="p-6 border border-danger-border bg-danger-bg rounded-lg text-danger-text flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2"><AlertTriangle size={16} aria-hidden />緊急応援要請の取得に失敗しました。</span>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>再試行</Button>
        </div>
      ) : requests.length === 0 ? (
        <EmptyState message="該当する要請はありません" description="欠員が出たときは「要請を作成」から近隣店舗・店舗内へ知らせられます。" />
      ) : (
        <div className="space-y-2">
          {requests.map((req) => <RequestCard key={req.request_id} req={req} onChanged={invalidate} />)}
        </div>
      )}

      {creating && <CreateRequestModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); invalidate(); }} />}
    </div>
  );
}
